import axios from 'axios';
import { debounce } from 'lodash';

// A token issued by a refresh and rejected within this window will not be fixed by refreshing again.
const REFRESHED_TOKEN_GRACE_MS = 60 * 1000;

// Shared by every component, so concurrent 401s wait on one refresh.
const tokenRefresh = {
	inFlight: null,
	rejectedTokens: new Set(),
	issuedToken: null,
	issuedAt: 0
};

function rememberRejectedToken(error) {
	const authorization = error.config?.headers?.Authorization;
	if (error.response?.status === 401 && authorization) {
		tokenRefresh.rejectedTokens.add(authorization.replace(/^Bearer /, ''));
	}
}

export default {
	data() {
		return {
			_api: {
				users: {
					key: this.$store.getters.getEnv('VUE_APP_USERS_API_KEY'),
					endpoint: this.$store.getters.getEnv('VUE_APP_USERS_API_URL'),
					timeout: 600000 // 10 minutes
				},
				creditors: {
					key: this.$store.getters.getEnv('VUE_APP_CREDITORS_API_KEY'),
					endpoint: this.$store.getters.getEnv('VUE_APP_CREDITORS_API_URL'),
					timeout: 600000 // 10 minutes
				}
			}
		}
	},
	methods: {
		getTimeoutErrorMessage(error) {
			return `Request (${error.config.method}) to ${error.config.url} has timed out.`;
		},
		_users_api() {
			const headers = {};
			if (this.$store.state.auth.access_token) {
				headers.Authorization = `Bearer ${this.$store.state.auth.access_token}`;
			}
			const instance = axios.create({
				baseURL: this._api.users.endpoint,
				timeout: this._api.users.timeout,
				headers: {
					'x-api-key': this._api.users.key,
					Accept: 'application/json',
					'Content-Type': 'application/json',
					...headers
				}
			});
			instance.interceptors.response.use(response => response, error => {
				if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
					console.log(this.getTimeoutErrorMessage(error));
				}
				rememberRejectedToken(error);
				return error.response;
			});
			return instance;
		},
		_creditors_api() {
			const headers = {};
			if (this.$store.state.auth.access_token) {
				headers.Authorization = `Bearer ${this.$store.state.auth.access_token}`;
			}
			const params = {};
			if (this.$store.state.creditor.creditor) {
				params.creditor_id = this.$store.state.creditor.creditor.creditor_id;
			}
			const instance = axios.create({
				baseURL: this._api.creditors.endpoint,
				params: {
					...params
				},
				timeout: this._api.creditors.timeout,
				headers: {
					'x-api-key': this._api.creditors.key,
					Accept: 'application/json',
					'Content-Type': 'application/json',
					...headers
				}
			});
			instance.interceptors.response.use(response => response, error => {
				if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
					console.log(this.getTimeoutErrorMessage(error));
				}
				rememberRejectedToken(error);
				return error.response;
			});
			return instance;
		},
		async refreshAccessToken() {
			if (tokenRefresh.inFlight) {
				return tokenRefresh.inFlight;
			}
			const currentToken = this.$store.state.auth.access_token;
			if (currentToken && !tokenRefresh.rejectedTokens.has(currentToken)) {
				// The 401 was for a request sent with an older token, so a retry picks up the current one.
				return true;
			}
			if (currentToken && currentToken === tokenRefresh.issuedToken && Date.now() - tokenRefresh.issuedAt < REFRESHED_TOKEN_GRACE_MS) {
				this.forceLogout(this);
				return false;
			}
			tokenRefresh.inFlight = this.requestNewAccessToken().finally(() => {
				tokenRefresh.inFlight = null;
			});
			return tokenRefresh.inFlight;
		},
		async requestNewAccessToken() {
			const { data, status } = await this._users_api().post('api/refresh', { refresh_token: this.$store.state.auth.refresh_token });
			switch (status) {
				case 200:
					this.$store.commit('updateAuthTokens', data);
					tokenRefresh.rejectedTokens.clear();
					tokenRefresh.issuedToken = data.access_token;
					tokenRefresh.issuedAt = Date.now();
					this.$store.commit('incrementStoreUpdateCount');
					return true;
				case 401:
					this.forceLogout(this);
					return false;
				default:
					return false;
			}
		},
		forceLogout: debounce(($this) => {
			$this.$router.push({ name: 'Logout' });
		}, 500)
	}
}